import { Stack } from '@mui/material';
import { get, isArray, set } from 'lodash';
import React, { FC, ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBackdrop from '../../../../components/ui-component/loading/LoadingBackdrop';
import MuiSnackbar from '../../../../components/ui-component/mui-snackbar/MuiSnackbar';
import MUIToolTip from '../../../../components/ui-component/mui-tooltip/MUIToolTip';
import { DuplicateAntrag } from '../../../../lib/api/antrag';
import { PostRecord } from '../../../../lib/api/PostRecord';
import { APIS } from '../../../../lib/constants/apis';
import { ANTRAG_VERBUNDPARTNER } from '../../../../lib/constants/componentsPath';
import {
	ANTRAG_BUTTON_NAME,
	ANTRAG_TYP,
	ANTRAGS_FORMULAREN,
	ANTRAGS_STATUS,
	MEDIAN_INTERVAL,
	MONAT_LISTE,
	NOCH_NICHT_MOEGLICH_TEXT,
	VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT,
} from '../../../../lib/constants/constant';
import { PAGES_URLS } from '../../../../lib/constants/routes';
import { enumT } from '../../../../utils/helper';
import { AntragButton, AntragsButtonWrapper, getMonatListe } from '../FoerderungUebersicht';

// Import schema
import Schema from '../../../../lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from '../../../../lib/paths/kostenposition_FULL_Paths.json';
import VerwendungsnachweisSchema from '../../../../lib/paths/verwendungsnachweis_FULL_Paths.json';
import ZwischenBerichtSchema from '../../../../lib/paths/zwischenbericht_FULL_Paths.json';
const P = Schema.attributes;
const VN_P = VerwendungsnachweisSchema.attributes;
const ZB_P = ZwischenBerichtSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const EmobFundEFoerderDashborad: FC<{ application: any; foerderProgramm: any }> = ({ application, foerderProgramm }) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [wsError, setWsError] = useState(false);

	const checkJahrCondition = false; // Set to true or false as needed
	const currentYear = new Date().getFullYear();

	const antrag = get(application, P.Skizze_Antrags.path);

	const projektDauer = get(antrag, P.Allgemein_Projektdauerin_Jahren.path, 0);
	const projektJahrStart = get(antrag, P.Allgemein_Projektstart_Jahr.path);

	const verwendungsnachweis = get(antrag, P.Foerderantrag_Verwendungsnachweiss.path, []);
	const zwischenberichte = get(antrag, P.Foerderantrag_Zwischenberichts.path, []);
	const abschlussbericht = get(antrag, P.Foerderantrag_Abschlussberichts.path, []);

	const handleCreateFoerderAntrag = async () => {
		try {
			setLoading(true);
			const antragformlar = get(application, P.Allgemein_Antragsformular.path, '');
			let res = await DuplicateAntrag(get(application, 'id'), antragformlar, ANTRAG_TYP.Skizze, foerderProgramm);
			if (res) {
				navigate(`${PAGES_URLS.OnlineAntrag}`, {
					state: {
						id: get(res, 'data.id'),
						api: APIS.Antrag,
						antragsformular: get(res, `data.${P.Allgemein_Antragsformular.path}`),
					},
				});
			}
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Antrag ', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateVerwendungsnachweis = async (projektJahr: number) => {
		const allKostenPostion = get(antrag, P.Foerderantrag_Kostenpositions.path);
		const VN_Data = {
			Allgemein_Haushaltsjahr: projektJahr,
			Verwendungsnachweisstatus_Status: ANTRAGS_STATUS.inBearbeitung,
			Foerderantrag: get(antrag, 'id'),
			Allgemein_Foerderquote_Antragstellerin: get(antrag, P.Orga_Foerderquote_Gesamt.path),
			Bankverbindung_Bank: get(antrag, P.Antragstellende_Organisation_Bankverbindung_Geldinstitut.path),
			Bankverbindung_IBAN: get(antrag, P.Antragstellende_Organisation_Bankverbindung_IBAN.path),
			Bankverbindung_BIC: get(antrag, P.Antragstellende_Organisation_Bankverbindung_BIC.path),
			Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung: get(antrag, P.Bewilligung_Jahresaufteilung.path, []).find(
				(ele: any) => get(ele, 'Projektjahr', '') === projektJahr
			)?.Zuwendung,
			Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr: get(
				verwendungsnachweis[verwendungsnachweis.length - 1],
				VN_P.Ermittlung_Des_Auszahlungsbetrages_Bereits_Ausgezahlte_Mittel.path,
				0
			),
			Block_1: [
				{
					Zelle_1: get(antrag, P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path, ''),
					Zelle_2: get(antrag, P.Antragstellende_Organisation_Foerderquote.path, []).find((ele: any) => get(ele, 'Projektjahr') === projektJahr)
						?.Foerderquote_403741,
				},
				...get(antrag, P.Verbundpartner.path, [])
					.filter((ele: any) => get(ele, ANTRAG_VERBUNDPARTNER.Name, '') !== '')
					.map((eachVP: any) => {
						return {
							Zelle_1: get(eachVP, ANTRAG_VERBUNDPARTNER.Name, ''),
							Zelle_2: get(eachVP, ANTRAG_VERBUNDPARTNER.FoerderQuoteBlock, []).find((ele: any) => get(ele, 'Projektjahr') === projektJahr)
								?.Foerderquote_403741,
						};
					}),
			],
		};
		try {
			let vnRes = await PostRecord(APIS.Verwendungsnachweis, { data: VN_Data });
			if (vnRes) {
				for (let i = 0; i < allKostenPostion.length; i++) {
					const eachKP = allKostenPostion[i];
					const nachweisData = {
						Allgemein_Nr: i + 1,
						Allgemein_Kostentraeger: get(eachKP, KP_P.Allgemein_Kostentraeger.path),
						Allgemein_Unternehmen_Institution: get(eachKP, `${KP_P.Unternehmen.path}.id`),
						Verwendungsnachweis: get(vnRes, 'data.id'),
						Allgemein_Kostenposition: get(eachKP, 'id'),
						Allgemein_Kostenart: get(eachKP, KP_P.Allgemein_Kostenposition.path),
						Allgemein_Bezeichnung: get(eachKP, KP_P.Allgemein_Bezeichnung.path),
						Kostennachweise_Nachweise: [{ Kommentar: '' }],
						Stundennachweis_Geleistete_Stunden: getMonatListe(eachKP),
					};
					await PostRecord(APIS.Nachweisposition, { data: nachweisData });
				}
				navigate(`${PAGES_URLS.OnlineAntrag}`, { state: { id: get(vnRes, 'data.id'), api: APIS.Verwendungsnachweis } });
			}
		} catch (err: any) {
			console.error('ERROR: Creating Verwendungsnachweis ', err);
		} finally {
		}
	};
	const handleCreateZwischenbericht = async () => {
		// autofold/fold_next
		let zbData = {
			Foerderantrag: get(antrag, 'id'),
			Zwischenberichtstatus_Status: 'inBearbeitung',
			Beschreibung_Der_Bislang_0007066993_se_Projektergebnisse_Block_1: get(antrag, P.Zeitplanung_Zeitplanung_Block_1.path, []).map((ele: any) => {
				return {
					Zelle_1: get(ele, 'Zelle_1', null),
					Zelle_2: get(ele, 'Zelle_4', null),
					Zelle_3: get(ele, 'Zelle_5', null),
				};
			}),
		};
		try {
			setLoading(true);
			let res = await PostRecord(APIS.Zwischenbericht, { data: zbData });
			if (res) navigate(`${PAGES_URLS.OnlineAntrag}`, { state: { id: get(res, 'data.id'), api: APIS.Zwischenbericht } });
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Zwischenbericht ', err);
		} finally {
			setLoading(false);
		}
	};
	const handleCreateAbschlussbericht = async () => {
		// autofold/fold_next
		let ABData = {
			Foerderantrag: get(antrag, 'id'),
			Abschlussberichtstatus_Abschlussberichtsstatus: ANTRAGS_STATUS.inBearbeitung,
			Antragsteller: get(antrag, `${P.Antragstellende_Organisation.path}.id`),
		};
		if (get(antrag, P.Allgemein_Antragsformular.path) === ANTRAGS_FORMULAREN.EmobFundE) {
			set(ABData, `Kurzfassung_Zum_Projektende.0.Kostentraeger`, 'Antragsteller');
			set(ABData, `Kurzfassung_Zum_Projektende.0.Antragsteller`, get(antrag, `${P.Antragstellende_Organisation.path}.id`));

			set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.0.Kostentraeger`, 'Antragsteller');
			set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.0.Antragsteller`, get(antrag, `${P.Antragstellende_Organisation.path}.id`));

			const allVpartnerAntrag = get(antrag, P.Verbundpartner.path, []);
			for (let [index, eachVPartner] of allVpartnerAntrag.entries()) {
				set(ABData, `Kurzfassung_Zum_Projektende.${index + 1}.Kostentraeger`, 'Verbundpartner');
				set(ABData, `Kurzfassung_Zum_Projektende.${index + 1}.Verbundpartner`, get(eachVPartner, 'Verbundpartner.id'));
				set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.${index + 1}.Kostentraeger`, 'Verbundpartner');
				set(ABData, `Effekte_Der_Foerderung_Im_Unternehmen.${index + 1}.Verbundpartner`, get(eachVPartner, 'Verbundpartner.id'));
			}
		}
		try {
			setLoading(true);
			let res = await PostRecord(APIS.Abschlussbericht, { data: ABData });
			if (res) navigate(`${PAGES_URLS.OnlineAntrag}`, { state: { id: get(res, 'data.id'), api: APIS.Abschlussbericht } });
		} catch (err: any) {
			setWsError(true);
			console.error('ERROR: Creating Abschlussbericht ', err);
		} finally {
			setLoading(false);
		}
	};

	const RenderVerwendungsnachweisButtons: FC<{}> = () => {
		const buttons: ReactElement[] = [];

		// Group Mittelabruf by Haushaltsjahr (project year)
		const groupedVerwendungsnachweisNachProjektJahr = verwendungsnachweis.reduce((acc: any, item: any) => {
			const year = get(item, VN_P.Allgemein_Haushaltsjahr.path);
			if (!acc[year]) {
				acc[year] = [];
			}
			acc[year].push(item);
			return acc;
		}, {});

		// Loop through each year of the project duration
		for (let i = 0; i < projektDauer; i++) {
			const currentProjektJahr = projektJahrStart + i; // Determine current year

			// Check if there's any Mittelabruf for the current year
			const verwendungsnachweisForYear = groupedVerwendungsnachweisNachProjektJahr[currentProjektJahr];

			// Determine whether the button to create Mittelabruf for this year should be enabled
			const isYearValidForCreation = checkJahrCondition ? currentProjektJahr === currentYear : true;

			// If there are Mittelabruf(s) for the year, show them
			if (verwendungsnachweisForYear && verwendungsnachweisForYear.length > 0) {
				// Checking if the last mittelabruf has the status "freigegeben"
				const isLastNachweisFrei =
					(
						get(verwendungsnachweisForYear[verwendungsnachweisForYear.length - 1], VN_P.Verwendungsnachweisstatus_Status.path, '') ===
							ANTRAGS_STATUS.freigegeben ||
						get(verwendungsnachweisForYear[verwendungsnachweisForYear.length - 1], VN_P.Verwendungsnachweisstatus_Status.path, '') ===
							ANTRAGS_STATUS.abgelehnt
					) ?
						true
					:	false;
				let isVNErstellenButtonDisabled: boolean = false;
				if (isLastNachweisFrei) {
					if (isVNErstellenButtonDisabled) {
						// if last mittelabruf is "freigegeben" but the current year is not valid then no MA can be created
						isVNErstellenButtonDisabled = true;
					} else {
						isVNErstellenButtonDisabled = false;
					}
				} else {
					// Disable the create button, if last MA for this year does not has the status "freigegeben"
					isVNErstellenButtonDisabled = true;
				}
				buttons.push(
					<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf und Verwendungsnachweis für ' + currentProjektJahr + ': '}>
						{verwendungsnachweisForYear.map((eachVN: any, index: number) => {
							return (
								<AntragButton
									key={`vn-${currentProjektJahr}-${index}`}
									name={`Projektjahr: ${get(eachVN, VN_P.Allgemein_Haushaltsjahr.path)} #${index + 1}`}
									description={enumT(get(eachVN, VN_P.Verwendungsnachweisstatus_Status.path))}
									onClick={() =>
										navigate(PAGES_URLS.OnlineAntrag, {
											state: {
												id: get(eachVN, `id`),
												api: APIS.Verwendungsnachweis,
											},
										})
									}
								/>
							);
						})}
						{get(antrag, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.inFoerderung && (
							/* VN-Erstellung temporär für alle Programme deaktiviert (ursprünglich: disabled={isVNErstellenButtonDisabled}) */
							<MUIToolTip title={VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT} showTootip={true}>
								<AntragButton
									name='Verwendungsnachweis erstellen'
									description=''
									onClick={() => handleCreateVerwendungsnachweis(currentProjektJahr)}
									disabled={true || isVNErstellenButtonDisabled}
									variant={'contained'}
								/>
							</MUIToolTip>
						)}
					</AntragsButtonWrapper>
				);
			} else {
				// If there are no Mittelabruf for the year, show the button to create a new one
				if (get(antrag, P.Antragstatus_Antragstatus.path) === ANTRAGS_STATUS.inFoerderung) {
					buttons.push(
						<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf und Verwendungsnachweis für ' + currentProjektJahr + ': '}>
							{/* VN-Erstellung temporär für alle Programme deaktiviert (ursprünglich: disabled={!isYearValidForCreation}) */}
							<MUIToolTip title={VN_ERSTELLUNG_NOCH_NICHT_MOEGLICH_TEXT} showTootip={true}>
								<AntragButton
									name='Verwendungsnachweis erstellen'
									description=''
									onClick={() => handleCreateVerwendungsnachweis(currentProjektJahr)}
									disabled={true || !isYearValidForCreation}
									variant={'contained'}
								/>
							</MUIToolTip>
						</AntragsButtonWrapper>
					);
				} else {
					buttons.push(
						<AntragsButtonWrapper key={currentProjektJahr} title={'Mittelabruf und Verwendungsnachweis für ' + currentProjektJahr + ': '}>
							<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} disabled={true} />
						</AntragsButtonWrapper>
					);
				}
			}
		}

		return <>{buttons}</>;
	};
	const RenderZwischenBerichtButtons: FC<{}> = () => {
		// autofold/fold_next
		const znErstellungInterval = MEDIAN_INTERVAL.halbjaehrlich;
		const monthInterval = Math.ceil(12 / znErstellungInterval);
		const totalPossibleReports = projektDauer * znErstellungInterval;
		const areReportsFull = zwischenberichte.length >= totalPossibleReports;

		// Function to determine if a create button should be active
		const isCreateButtonActive = (reportIndex: number, yearIndex: number) => {
			const reportYear = projektJahrStart + yearIndex;
			const isCurrentYear = checkJahrCondition ? reportYear === currentYear : true;
			return !areReportsFull && reportIndex === zwischenberichte.length && isCurrentYear;
		};

		return (
			<AntragsButtonWrapper title='Zwischenbericht:'>
				{zwischenberichte.length > 0 ?
					<>
						{Array.from({ length: projektDauer }).map((_, yearIndex) => (
							<Stack direction='row' justifyContent='space-between' key={yearIndex} spacing={2}>
								{Array.from({ length: znErstellungInterval }).map((_, buttonIndex) => {
									const monthIndex = (buttonIndex + 1) * monthInterval - 1;
									const monthName = MONAT_LISTE[monthIndex]?.Monat || 'Unbekannter Monat';
									const reportIndex = yearIndex * znErstellungInterval + buttonIndex;

									if (reportIndex < zwischenberichte.length) {
										return (
											<AntragButton
												key={`report-${yearIndex}-${buttonIndex}`}
												name={`${projektJahrStart + yearIndex} für Ende ${monthName}`}
												description={`Status: ${enumT(get(zwischenberichte, `${reportIndex}.${ZB_P.Zwischenberichtstatus_Status.path}`))}`}
												onClick={() => {
													navigate(`${PAGES_URLS.OnlineAntrag}`, {
														state: { id: get(zwischenberichte, `${reportIndex}.id`), api: APIS.Zwischenbericht },
													});
												}}
											/>
										);
									} else {
										return (
											<AntragButton
												key={`erstellen-${yearIndex}-${buttonIndex}`}
												name={`${projektJahrStart + yearIndex} für Ende ${monthName}`}
												description=''
												onClick={handleCreateZwischenbericht}
												variant={'contained'}
												disabled={!isCreateButtonActive(reportIndex, yearIndex)}
											/>
										);
									}
								})}
							</Stack>
						))}
					</>
				:	<>
						{get(antrag, P.Antragstatus_Antragstatus.path) !== ANTRAGS_STATUS.inFoerderung ?
							<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} disabled={true} />
						:	<>
								{Array.from({ length: projektDauer }).map((_, yearIndex) => (
									<Stack direction='row' justifyContent='space-between' key={yearIndex} spacing={2}>
										{Array.from({ length: znErstellungInterval }).map((_, buttonIndex) => {
											const monthIndex = (buttonIndex + 1) * monthInterval - 1;
											const monthName = MONAT_LISTE[monthIndex]?.Monat || 'Unbekannter Monat';
											const reportIndex = yearIndex * znErstellungInterval + buttonIndex;

											return (
												<AntragButton
													key={`erstellen-${yearIndex}-${buttonIndex}`}
													name={`${projektJahrStart + yearIndex} für Ende ${monthName}`}
													description=''
													onClick={handleCreateZwischenbericht}
													variant={'contained'}
													disabled={!isCreateButtonActive(reportIndex, yearIndex)}
												/>
											);
										})}
									</Stack>
								))}
							</>
						}
					</>
				}
			</AntragsButtonWrapper>
		);
	};
	const RenderAbschlussberichtButton: FC<{}> = () => {
		// autofold/fold_next
		if (isArray(abschlussbericht) && abschlussbericht.length > 0) {
			return (
				<AntragsButtonWrapper title='Abschlussbericht:'>
					{abschlussbericht.map((eachAbschlussbericht: any, i: number) => {
						return (
							<AntragButton
								key={i}
								name='Abschlussbericht'
								description='Ansehen'
								onClick={() =>
									navigate(`${PAGES_URLS.OnlineAntrag}`, {
										state: { id: get(eachAbschlussbericht, 'id'), api: APIS.Abschlussbericht },
									})
								}
							/>
						);
					})}
				</AntragsButtonWrapper>
			);
		} else {
			if (get(antrag, P.Antragstatus_Antragstatus.path, '') === ANTRAGS_STATUS.ProjektBeendet) {
				return (
					<AntragsButtonWrapper title='Abschlussbericht:'>
						<AntragButton
							name='Abschlussbericht'
							description='Abschlussbericht erstellen'
							onClick={handleCreateAbschlussbericht}
							variant='text'
						/>
					</AntragsButtonWrapper>
				);
			} else {
				return (
					<AntragsButtonWrapper title='Abschlussbericht:'>
						<AntragButton name={NOCH_NICHT_MOEGLICH_TEXT} description='offen' onClick={() => {}} variant='text' disabled={true} />
					</AntragsButtonWrapper>
				);
			}
		}
	};

	return (
		<>
			<MuiSnackbar error={true} showSnackbar={wsError} closeSnackbar={() => setWsError(false)} alertMessage='Etwas ist schief gelaufen' />
			<LoadingBackdrop open={loading} />
			{/* Antrag und Skizze */}
			<Stack direction={'row'} justifyContent={'space-between'}>
				<AntragsButtonWrapper title='Skizze:'>
					<AntragButton
						name={get(application, P.Allgemein_Projekttitel.path) ? get(application, P.Allgemein_Projekttitel.path) : ANTRAG_BUTTON_NAME}
						description={enumT(get(application, P.Antragstatus_Antragstatus.path))}
						onClick={() =>
							navigate(PAGES_URLS.OnlineAntrag, {
								state: {
									id: get(application, 'id'),
									api: APIS.Antrag,
									antragsformular: get(application, P.Allgemein_Antragsformular.path),
								},
							})
						}
					/>
				</AntragsButtonWrapper>

				{get(application, `${P.Skizze_Antrags.path}.id`) ?
					<>
						<AntragsButtonWrapper title='Antrag:'>
							<AntragButton
								name={
									get(application, P.Skizze_Antrags.schema.attributes.Allgemein_Projekttitel.path) ?
										get(application, P.Skizze_Antrags.schema.attributes.Allgemein_Projekttitel.path)
									:	ANTRAG_BUTTON_NAME
								}
								description={enumT(get(application, P.Skizze_Antrags.schema.attributes.Antragstatus_Antragstatus.path))}
								onClick={() =>
									navigate(`${PAGES_URLS.OnlineAntrag}`, {
										state: {
											id: get(application, `${P.Skizze_Antrags.path}.id`),
											api: APIS.Antrag,
											antragsformular: get(application, P.Allgemein_Antragsformular.path),
										},
									})
								}
							/>
						</AntragsButtonWrapper>
					</>
				:	<>
						{get(application, P.Antragstatus_Antragstatus.path) === 'EmpfehlungZurAntragstellung' ?
							<AntragsButtonWrapper title='Antrag:'>
								<AntragButton name={'Antrag erstellen'} description={''} onClick={handleCreateFoerderAntrag} variant={'contained'} />
							</AntragsButtonWrapper>
						:	<AntragsButtonWrapper title='Antrag:'>
								<AntragButton name={'Noch nicht möglich'} description={''} onClick={() => {}} disabled={true} />
							</AntragsButtonWrapper>
						}
					</>
				}
			</Stack>

			{/* Mittelabruf und Verwendungsnachweis */}
			<RenderVerwendungsnachweisButtons />

			<RenderZwischenBerichtButtons />
			<RenderAbschlussberichtButton />
		</>
	);
};

export default EmobFundEFoerderDashborad;
